/**
 * Centralized API client for all API requests
 */
import axios from 'axios';
import Constants from '../Constants.json';

const apiClient = axios.create({
  baseURL: Constants.BACKEND_SERVER_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: Constants.APP_API_TIMEOUT || 200000,
});

function clearAuthAndRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('expirationTime');
  localStorage.removeItem('role');
  window.location.href = '/login';
}

function accessTokenExpirationIso(accessToken) {
  try {
    const payload = JSON.parse(
      atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (payload.exp) return new Date(payload.exp * 1000).toISOString();
  } catch (_) {
    /* ignore */
  }
  return new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString();
}

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) return null;

  refreshPromise = (async () => {
    try {
      const base = (Constants.BACKEND_SERVER_BASE_URL || '').replace(/\/$/, '');
      const res = await axios.post(`${base}/token/refresh/`, { refresh });
      const access = res.data?.access;
      if (!access) return null;
      const nextRefresh = res.data.refresh || refresh;
      localStorage.setItem('token', access);
      localStorage.setItem('refreshToken', nextRefresh);
      localStorage.setItem('expirationTime', accessTokenExpirationIso(access));
      return access;
    } catch (_) {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: try silent refresh once on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      const access = await refreshAccessToken();
      if (access) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${access}`;
        return apiClient(original);
      }
      clearAuthAndRedirect();
    } else if (error.response?.status === 401) {
      clearAuthAndRedirect();
    }
    return Promise.reject(error);
  }
);

export default apiClient;
